// medplum-encounter.js — ONLINE-PRIMARY consult/prescription read path.
// The browser reads ONE encounter's whole clinical graph DIRECTLY from Medplum
// (RLS-scoped by the doctor's own token via the shared getMedplum() session)
// and shapes it CLIENT-SIDE into the exact buildEncounterView DTO that
// ConsultCompact.jsx / consult-kit.jsx / Writer.jsx already read through
// OPDBridge.pick('encounter.…'). The screen JSX is unchanged (the mockups ARE
// the design — AGENTS.md §7); this just replaces the Express bootstrap fetch
// for the `encounter` route.
//
// ONE round trip: a single Encounter search with _include (Patient,
// Practitioner) + _revinclude (every child resource type the encounter view
// needs). The encounterview spike proved a GraphQL fan-out and a batch-of-7 are
// both ~1 round trip; we use the _revinclude search because it (a) returns FULL
// resources in one bundle — GraphQL only returned ids in the spike — and (b)
// matches the exact idiom the live queue/reception/patients/schedule/reports
// loaders already use (medplum-queue.js), so there's one fetch pattern in this
// codebase, not two.
//
// LEGACY-URL COMPAT SHIM (slug pass). A FHIR-direct read needs the Medplum
// Encounter UUID, and as of 2026-07-10 EVERY live producer of a /consult href
// emits UUIDs — queue/reception/reports read Medplum resources (`enc.id`), and
// the incoming-referrals worklist now emits `targetEncounterId` = the Medplum
// UUID too (server shapeWorklistRow → encounterSlugToUuid; the disk slug no
// longer crosses to the client — AGENTS §3 keeps it as the native disk key only).
// So the first pass (search by `_id`=UUID) is the ONLY path a fresh navigation
// takes. The slug pass below survives purely as a compatibility shim for (a) old
// bookmarked /consult/<opd-slug> links a doctor may still have, and (b) the rare
// not-yet-synced offline row whose server helper fell back to the slug. It
// resolves slug→UUID via the OPD **encounter-slug** identifier the mirror stamps
// (`https://healther.io/opd/encounter-slug|<slug>`), mirroring the server's
// getEncounterBySlug / encounterSlugToUuid (server ID_SYSTEMS.ENCOUNTER_SLUG,
// medplum-store.mjs). NOTE: `https://healther.io/opd/token` is the queue *display* token
// carried as an Encounter EXTENSION, not an identifier — searching it here
// silently matched nothing and was the root cause of referral consult links
// dead-ending on "not found or not authorized".
import { getMedplum, deriveCurrentUser } from './client-port.js';
import { grantChartAccess, ACCESS_INDEX_SETTLE_MS } from './medplum-access.js';
import { buildEncounterView } from '@healther/clinical-core/shape/encounter-view.js';
import { loadFavouritesForConsult } from './medplum-settings.js';
import { loadPatientHistoryFromMedplum } from './medplum-history.js';
import { enrichConsultView } from './medplum-consult-enrichment.js';

// How many PRIOR visits the Prior tab lists — loadPatientHistoryFromMedplum's own
// default `limit`, which ConsultPrior.jsx relies on. The Prior·N badge must be
// derived against the same number or the count contradicts the list it captions
// (see the historyPromise comment below).
const PRIOR_VISIT_LIMIT = 20;

// Derive the returning-patient priorContext from the patient's prior-visit list
// (newest-first, the CURRENT encounter already excluded by the loader). Returns
// null when there is no prior history, so the caller leaves buildEncounterView's
// tolerated empty stub in place and the header strip / Prior badge stay quiet for
// a genuine first-timer. Shape mirrors the server DTO the screens read
// (ConsultPrior PriorContextStrip + the Prior·N tab badge in ConsultCompact).
function buildPriorContext(visits) {
  const list = Array.isArray(visits) ? visits : [];
  if (!list.length) return null;
  const newest = list[0];
  return {
    returning: true,
    visitCount: list.length,
    lastVisit: newest && newest.date ? { date: newest.date, department: newest.department || null } : null,
    lastDx: (newest && (newest.diagnosis || null)) || null,
    pastLine: null,
  };
}

// Encounter identifier system the disk→Medplum mirror stamps for slug↔UUID
// resolution — MUST match server ID_SYSTEMS.ENCOUNTER_SLUG / client
// SLUG_SYSTEMS.ENCOUNTER (outbox-ops.js), or slug hrefs never resolve.
const ENCOUNTER_SLUG_SYSTEM = 'https://healther.io/opd/encounter-slug';

// The child resource types the encounter view fans in via _revinclude on the
// ONE graph search. Kept as a list so the search string and the bundle-grouping
// switch stay in lock-step.
//
// SCOPED TO THE ACCESS POLICY (verified live 2026-07-05): the demo
// `opd-doctor-rowlevel` AccessPolicy covers Composition/MedicationRequest/
// Observation/DocumentReference/ServiceRequest/QuestionnaireResponse — but NOT
// Media. Under Medplum RLS, _revinclude'ing a resource type the policy doesn't
// grant makes the WHOLE search 403 Forbidden (not just omit that type). So
// these five ride in the main graph search; QuestionnaireResponse is side-
// fetched best-effort (see loadEncounterFromMedplum) so older deployments whose
// policy still lacks the QR arm degrade gracefully instead of 403'ing the whole
// consult. Media stays side-fetched for the same reason.
const REVINCLUDES = [
  'Composition:encounter',
  'MedicationRequest:encounter',
  'Observation:encounter',
  'DocumentReference:encounter',
  'ServiceRequest:encounter',
];

// ── WRONG-SURFACE GUARD (a /consult/:id deep link that is NOT an OPD encounter) ─
// The consult room (ConsultCompact / Writer / PhotoFlow — every `encounter`
// bootstrap route under /consult/*) is the OUT-PATIENT surface: it renders an
// AMB (ambulatory) Encounter. An INPATIENT (class=IMP) or EMERGENCY (class=EMER)
// Encounter opened at /consult/:id is a *wrong-surface* deep link — a bookmarked
// or hand-typed URL, or a stale link — and must never render as an OPD consult.
// Each such encounter already has a real home keyed by the SAME Encounter UUID:
//   • IMP  → the bed workspace   /ward/bed/:id     (BedWorkspace: getBedContext(encId))
//   • EMER → the ED per-arrival  /emergency/:id    (EmergencyPatient: encounterIdFromPath)
// so the redirect target is simply the encounter id under the right prefix. Every
// role that can reach /consult (CLINICAL = doctor/owner/admin) is also admitted to
// both target routes (WARD = nurse/doctor/owner/admin; ED adds front-desk), so the
// redirect never dead-ends at the App.jsx role gate. Codes are v3-ActCode
// (Encounter.class is a single Coding in FHIR R4).
const WRONG_SURFACE = {
  IMP: (encId) => `/ward/bed/${encodeURIComponent(encId)}`,
  EMER: (encId) => `/emergency/${encodeURIComponent(encId)}`,
};

// Pure routing decision (unit-tested): given an Encounter resource, return the
// surface it should be redirected to when opened at /consult/:id, or null when
// the consult room IS its correct surface (AMB and anything unrecognised — we
// only divert the two classes that have a purpose-built home, never guess).
export function wrongSurfaceTarget(encounter) {
  const code = String((encounter && encounter.class && encounter.class.code) || '').toUpperCase();
  const make = WRONG_SURFACE[code];
  return (make && encounter && encounter.id) ? make(encounter.id) : null;
}

// Thrown by the consult loader when the deep-linked encounter belongs to a
// different surface. App.jsx ScreenBoot catches it (instanceof) and SPA-redirects
// to `target` — a plain <Navigate replace>, which NEVER touches the Medplum
// session. This is deliberately its OWN typed error (not NotLoggedInError, not a
// generic Error): a wrong surface is neither an auth failure (→ /login) nor a
// broken link (→ the "couldn't load" state) — it is a routing correction.
export class WrongSurfaceEncounterError extends Error {
  constructor({ target, encounterClass, encounterId }) {
    super(`encounter ${encounterId} is a ${encounterClass} record — belongs at ${target}, not the OPD consult`);
    this.name = 'WrongSurfaceEncounterError';
    this.target = target;
    this.encounterClass = encounterClass;
    this.encounterId = encounterId;
  }
}

// Groups one Encounter?_id=X&_include=…&_revinclude=… bundle into the resource
// sets buildEncounterView expects. Patient/Practitioner arrive via _include;
// the child types via _revinclude.
function groupEncounterGraph(bundle, encId) {
  const g = {
    encounter: null, patient: null, practitionerDisplay: null,
    questionnaireResponses: [], compositions: [], medicationRequests: [],
    observations: [], documentReferences: [], serviceRequests: [], media: [],
  };
  for (const entry of (bundle && bundle.entry) || []) {
    const r = entry.resource; if (!r) continue;
    switch (r.resourceType) {
      case 'Encounter': if (!g.encounter || r.id === encId) g.encounter = r; break;
      case 'Patient': g.patient = r; break;
      case 'Practitioner': {
        // Header doctor line — the Encounter.participant display is usually set,
        // but a bare reference falls back to the _include'd Practitioner name.
        const n = r.name && r.name[0];
        g.practitionerDisplay = (n && (n.text || [(n.given || []).join(' '), n.family].filter(Boolean).join(' '))) || g.practitionerDisplay;
        break;
      }
      case 'QuestionnaireResponse': g.questionnaireResponses.push(r); break;
      case 'Composition': g.compositions.push(r); break;
      case 'MedicationRequest': g.medicationRequests.push(r); break;
      case 'Observation': g.observations.push(r); break;
      case 'DocumentReference': g.documentReferences.push(r); break;
      case 'ServiceRequest': g.serviceRequests.push(r); break;
      case 'Media': g.media.push(r); break;
      default: break;
    }
  }
  return g;
}

// LEGACY-URL COMPAT (see the header note): slug→UUID resolution. Two live
// producers still reach this: (a) old bookmarked /consult/<opd-slug> links, and
// (b) a not-yet-synced OFFLINE walk-in navigated to by its optimistic enc-… slug
// before the outbox replays it and a real UUID exists. The ONLINE walk-in path no
// longer emits slug hrefs — callPatientIn resolves the real Encounter UUID from
// the outbox terminal snapshot before navigating (M-6). Mirrors server
// getEncounterBySlug / encounterSlugToUuid: the disk→Medplum mirror stamps the
// OPD encounter slug as an Encounter identifier under ENCOUNTER_SLUG_SYSTEM.
// Search by that identifier; return the UUID or null. RLS-scoped to the caller —
// a forbidden (out-of-department) encounter returns null here just like a
// non-existent one, which is exactly what the on-open grant retry in
// loadEncounterFromMedplum keys off.
async function resolveEncounterUuid(medplum, id) {
  const bundle = await medplum.search('Encounter', `identifier=${ENCOUNTER_SLUG_SYSTEM}|${encodeURIComponent(id)}&_count=1`);
  const entry = (bundle && bundle.entry) || [];
  const enc = entry.map((e) => e.resource).find((r) => r && r.resourceType === 'Encounter');
  return enc ? enc.id : null;
}

// Exported for the rare caller that only has an OPD encounter SLUG in hand
// (e.g. Tasks.jsx's follow-up booking, which talks to the legacy Express
// /scheduling/book endpoint) and needs the Medplum UUID to build a /consult
// href — same identifier-system search as the compat shim above, just
// reachable from outside this module instead of being re-implemented at the
// call site. Returns null (never throws) when the slug hasn't mirrored yet
// or the caller can't read it — callers should fall back to the slug.
export async function resolveEncounterSlugToUuid(slug) {
  if (!slug) return null;
  try {
    const { medplum } = await getMedplum();
    return await resolveEncounterUuid(medplum, slug);
  } catch (_) {
    return null;
  }
}

// The ONE graph-search query string. Module-level (not a closure inside the
// loader) so the on-open read and the pointer-down warm below produce a
// byte-identical URL — MedplumClient keys its request cache on that URL, so an
// identical string is the whole mechanism by which the warm read is reused.
function encounterGraphQuery(encId) {
  return `_id=${encodeURIComponent(encId)}&_include=Encounter:subject&_include=Encounter:participant`
    + REVINCLUDES.map((r) => `&_revinclude=${r}`).join('')
    + '&_count=200';
}

/**
 * Warm the consult's ROOT read before the navigation that needs it.
 *
 * The queue row calls this on pointer-down; the click, the SPA route change and
 * ScreenBoot's mount all happen after it, so by the time loadEncounterFromMedplum
 * issues the same search, MedplumClient serves the already-in-flight (or already
 * settled) promise — it caches the ReadablePromise itself, so this DEDUPES rather
 * than duplicating the request. Net effect: the encounter graph read leaves the
 * post-click critical path, which under SPA nav is the first of only three
 * remaining serialized round trips.
 *
 * DELIBERATELY READ-ONLY. This is the same RLS-scoped GET the loader would make
 * as the same logged-in user, for an encounter whose summary the row is already
 * rendering — it grants nothing, writes nothing and creates no audit/notify Task.
 * That matters because pointer-down also fires when a finger starts a SCROLL on a
 * tablet: warming a read there is harmless, whereas warming the on-open access
 * GRANT (a Bot write that notifies the care team) would not be, so the grant
 * deliberately stays on the click path inside the loader.
 *
 * Best-effort by design: a failure here is never surfaced, because the real load
 * repeats the identical read moments later and fails loud there.
 */
export function prefetchConsultEncounter(id) {
  const encId = String(id || '').trim();
  if (!encId) return;
  getMedplum()
    .then(({ medplum }) => medplum.search('Encounter', encounterGraphQuery(encId)))
    .catch(() => { /* the real load re-issues this read and reports any error */ });
}

// Loads + shapes one encounter's graph. `id` is the /consult/:id route param
// (Medplum UUID or OPD slug). `patientId` (OPTIONAL, a Medplum Patient UUID)
// enables the on-open grant retry below — see the comment there for exactly
// what is and is not auto-granted. Returns the same shape
// installBridge()/OPDBridge expects for the `encounter` route bootstrap, with
// the extra draftRef pointer (encounter.draftRef.{id,versionId,code}) the
// write layer needs for If-Match.
export async function loadEncounterFromMedplum({ id, patientId = null }) {
  const { medplum, hospital } = await getMedplum();

  // ── BOOT LATENCY: user-scoped reads start NOW, not after the chart ────────
  // deriveCurrentUser (PractitionerRole → Organization) and the doctor's saved
  // favourites (Basic?author=%profile) depend on the SESSION only — nothing in
  // the encounter graph. They used to be awaited near the end of this function,
  // which put two more serialized round trips on the consult's critical path
  // (and deriveCurrentUser is itself two hops when its per-session memo is cold,
  // i.e. on every full page load). Kick both off here and await them in the one
  // fan-in below, so they overlap the graph read + grant instead of following
  // them. Neither can affect the reads below, so there is no ordering to lose.
  const currentUserPromise = deriveCurrentUser(medplum);
  const favouritesPromise = loadFavouritesForConsult();
  // A rejection before the fan-in `await` would otherwise be an unhandled
  // rejection; the fan-in still sees the real error (this is a no-op observer,
  // NOT a swallow — deriveCurrentUser's NotLoggedInError still reaches ScreenBoot).
  currentUserPromise.catch(() => {});

  // FHIR-DIRECT read (no Bot): the encounter + patient + practitioner + every
  // child resource in ONE call, RLS-scoped to this doctor by their own token.
  // (encounterGraphQuery is module-level so prefetchConsultEncounter warms the
  // EXACT same cache key — see its note.)
  const query = encounterGraphQuery;

  // One resolution pass: search by UUID first; if the route param was an OPD
  // slug (not the Medplum UUID), resolve slug→UUID and re-fetch. Returns
  // { encId, bundle } or null when nothing readable matched.
  const findEncounter = async () => {
    let encId = id;
    let bundle = await medplum.search('Encounter', query(encId));
    let hasEnc = ((bundle && bundle.entry) || []).some((e) => e.resource && e.resource.resourceType === 'Encounter' && e.resource.id === encId);
    if (!hasEnc) {
      const uuid = await resolveEncounterUuid(medplum, id);
      if (uuid) { encId = uuid; bundle = await medplum.search('Encounter', query(encId)); hasEnc = true; }
    }
    return hasEnc ? { encId, bundle } : null;
  };

  let found = await findEncounter();

  // ON-OPEN GRANT (owner-decided; mechanics proven by
  // scripts/medplum/_spike-grant-revoke-bot.mjs): an empty result here is
  // either a broken link or an RLS-forbidden (out-of-department) encounter —
  // under row-level security the two are indistinguishable (the search simply
  // returns nothing). When the CALLER passed the patient's UUID we attempt the
  // access-grant Bot ONCE (it stamps this doctor's own %profile onto the whole
  // patient compartment — tamper-proof, requester is server-set), wait for the
  // compartment index to settle, and retry the resolution once.
  //
  // What this path does NOT auto-grant: a bare /consult/<encounterId> deep-link
  // with no patientId. Without a patientId there is nothing to grant against —
  // the Encounter read itself is RLS-forbidden, so the patient cannot be
  // derived from it — and inventing a lookaround would weaken the fail-loud
  // contract. Callers that already hold the patient row (queue, timeline's
  // "Open this consult") must thread patientId through the loader chain
  // (bridge.js MEDPLUM_LOADERS → this option) to enable the grant here.
  // A failed/unauthorized grant throws its own loud error (medplum-access.js).
  if (!found && patientId) {
    // AUDIT_2026-08-12 #13b — tell "the Bot said no" apart from "the Bot is
    // unreachable". A real denial (err.authorized === false, the Bot RAN and
    // refused) keeps its existing loud throw. Any OTHER failure — network,
    // Bot 404/500, cfg.bots.accessGrant unseeded (the local-Docker case) — is
    // the ACCESS SERVICE being down, not an access decision; rethrow it as a
    // specific, actionable message instead of letting it surface as the
    // misleading generic "not found or not authorized".
    try {
      await grantChartAccess({ patientId });
    } catch (e) {
      if (e && e.authorized === false) throw e;
      const err = new Error('The access service is not reachable, so this chart cannot be opened right now. Retry in a moment — your access itself is unchanged.');
      err.accessServiceDown = true;
      err.cause = e;
      throw err;
    }
    await new Promise((r) => setTimeout(r, ACCESS_INDEX_SETTLE_MS));
    // Bust MedplumClient's 60s search cache so the retry actually re-reads.
    if (typeof medplum.invalidateSearches === 'function') {
      try { medplum.invalidateSearches('Encounter'); } catch { /* best-effort */ }
    }
    found = await findEncounter();
  }

  if (!found) {
    // Fail loud: an unresolvable id is a real error (a broken link or an
    // RLS-forbidden encounter), not a state we should paper over with an empty
    // chart — the redesign invariant is fail-loud, no speculative fallback.
    throw new Error(`encounter ${id} not found or not authorized`);
  }
  let { encId, bundle } = found;

  // An assigned doctor can read an OPD Encounter through its practitioner or
  // department RLS arm even when a newly-booked Patient has no account stamp.
  // That incomplete state is unsafe to consult from: the sign Bot must search
  // every AllergyIntolerance and may create FamilyMemberHistory, whose policy
  // arms depend on the Patient compartment. Ensure the existing requester-bound
  // access-grant Bot stamps this authenticated doctor onto the Patient as the
  // chart opens. It can only grant event.requester to themself; no policy is
  // widened and no browser/service credential is introduced.
  //
  // Do this BEFORE shaping the graph/draftRef. $set-accounts propagate:true
  // advances the version of existing compartment resources, including a saved
  // consult draft; refetching after index settlement ensures later If-Match
  // saves/signs use the exact current version instead of a stale pre-grant one.
  const rawEncounter = (bundle.entry || [])
    .map((entry) => entry.resource)
    .find((resource) => resource?.resourceType === 'Encounter' && resource.id === encId);

  // WRONG-SURFACE GUARD (see WRONG_SURFACE above). An IMP/EMER encounter opened at
  // /consult/:id must redirect to its real home — the bed workspace / ED arrival —
  // NOT render as an OPD consult. We divert HERE, before the on-open grant and the
  // child side-fetches: not merely to skip wasted work, but because bailing out
  // early keeps a wrong-surface deep link on a pure routing path (and must never
  // stamp a chart-access grant for a chart we are about to redirect away from).
  // Throwing a typed error hands the redirect to ScreenBoot with the session
  // untouched (a genuine auth failure still throws NotLoggedInError from
  // getMedplum on its own path — this guard neither creates nor swallows one).
  const surface = wrongSurfaceTarget(rawEncounter);
  if (surface) {
    const encClass = String((rawEncounter && rawEncounter.class && rawEncounter.class.code) || '').toUpperCase();
    throw new WrongSurfaceEncounterError({ target: surface, encounterClass: encClass, encounterId: encId });
  }

  let subjectPatientId = null;
  // AUDIT_2026-08-12 #13b: true when the on-open access-grant Bot could not be
  // reached for a chart whose RLS-scoped read ALREADY SUCCEEDED. The consult
  // must render that loaded chart with a visible warning, not unmount to a
  // blank "not found" — before this flag, an unreachable Bot here threw and
  // destroyed a fully-loaded same-department consult (and made the consult
  // untestable on any deployment without the Bot seeded, e.g. local Docker).
  let accessServiceDown = false;
  if (rawEncounter) {
    const patientRef = String(rawEncounter.subject?.reference || '');
    const patientMatch = /^Patient\/([^/]+)$/.exec(patientRef);
    if (!patientMatch) throw new Error(`encounter ${encId} has no Patient subject`);
    subjectPatientId = patientMatch[1];
    let access = null;
    try {
      access = await grantChartAccess({
        patientId: subjectPatientId,
        reason: 'Opening assigned clinical encounter',
      });
    } catch (e) {
      // A real Bot denial stays loud — the doctor could read the Encounter but
      // the grant authority refused the compartment stamp; papering over that
      // would hide a policy contradiction. Everything else (network, unseeded
      // Bot, Bot 500) degrades to the warning banner: the chart data in hand is
      // authorized-by-construction (this doctor's own RLS token read it).
      if (e && e.authorized === false) throw e;
      accessServiceDown = true;
      // Keep the diagnostic: the banner tells the doctor, this tells the dev.
      try { console.warn('consult on-open access grant failed (service down, chart still shown):', e); } catch { /* console absent in exotic hosts */ }
    }
    if (access && !access.alreadyGranted) {
      await new Promise((resolve) => setTimeout(resolve, ACCESS_INDEX_SETTLE_MS));
      if (typeof medplum.invalidateSearches === 'function') {
        try { medplum.invalidateSearches(); } catch { /* best-effort cache bust; network refetch remains authoritative */ }
      }
      const refreshed = await findEncounter();
      if (!refreshed) throw new Error(`encounter ${id} not readable after access grant`);
      ({ encId, bundle } = refreshed);
    }
  }

  const graph = groupEncounterGraph(bundle, encId);

  // ── ONE FAN-IN (boot latency) ────────────────────────────────────────────
  // Everything below depends only on {encId, patientUuid}, both known now, so it
  // all rides ONE round trip instead of the three serialized ones this used to be
  // (side-fetch → history+user+favourites → enrichment). buildEncounterView
  // derives patient.id from exactly this subject reference (shape/encounter-view
  // .js: enc.subject.reference.split('/')[1]), so reading it from the raw
  // Encounter here is the same value, just available one stage earlier.
  //
  // Ordering that is deliberately KEPT: this batch still runs AFTER the on-open
  // grant above, because the patient-compartment reads (history, the enrichment's
  // Observation/CarePlan queries, QR/Media) are exactly what that grant makes
  // readable — overlapping them with the grant would silently return empty
  // panels for a chart the doctor has not been stamped onto yet.
  const patientUuid = subjectPatientId;

  // QuestionnaireResponse (intake "her exact words" / live facts) and Media
  // (consult transcript bubbles, intake audio) are side-fetched best-effort.
  // QR: kept OUT of REVINCLUDES so deployments whose policy still lacks the QR
  // read arm degrade gracefully (a forbidden _revinclude 403's the whole graph).
  // Media: still not in the demo AccessPolicy. We swallow ONLY 403 Forbidden and
  // rethrow anything else, so a genuine error still fails loud.
  const encRef = `Encounter/${encId}`;
  const sideFetch = async (type) => {
    try {
      const b = await medplum.search(type, `encounter=${encRef}&_count=200`);
      return ((b && b.entry) || []).map((e) => e.resource).filter(Boolean);
    } catch (e) {
      if (e && (e.status === 403 || /forbidden/i.test(e.message || ''))) return [];
      throw e;
    }
  };

  // LONGITUDINAL priorContext (best-effort, fails to the empty stub).
  // buildEncounterView emits priorContext empty because its graph is THIS
  // encounter only — so the returning-patient header strip (ConsultPrior
  // PriorContextStrip) and the "Prior·N" tab badge (ConsultCompact) render blank
  // / "Prior·0" even for a rich returning patient whose Prior tab (a separate
  // patient-wide query) is full. We reuse the SAME loader the Prior tab uses so
  // the count + last-visit are guaranteed CONSISTENT with the tab. Any failure
  // resolves to null and leaves the tolerated empty shape, so the consult can
  // never regress on this path.
  // Load the patient's WHOLE visit history ONCE (current visit included) — it
  // drives BOTH the priorContext strip AND the "what changed since last visit"
  // diff in the enrichment overlay, so we never query encounters twice.
  // COUNT/CONTENT AGREEMENT (fixed 2026-08-05). The comment above promises the
  // badge is "guaranteed CONSISTENT with the tab", but the two calls were
  // parameterised differently and silently disagreed:
  //   • the Prior TAB calls loadPatientHistoryFromMedplum(pid, { excludeEncounterId }),
  //     which fetches limit+1 and drops the current visit BEFORE capping → 20 rows;
  //   • this call fetched a flat 20 and dropped the current visit AFTER (line ~474),
  //     → 19.
  // So any patient with ≥20 prior visits saw "Prior·19" captioning a list of 20.
  // We cannot pass excludeEncounterId here, because this same history feeds
  // buildWhatChanged, which diffs the CURRENT visit against the prior one and
  // therefore needs the current visit present (consult-enrichment.js:447). So we
  // apply the identical +1 compensation the history loader applies for itself:
  // fetch PRIOR_VISIT_LIMIT + 1 so that after the current visit is filtered out
  // below, exactly PRIOR_VISIT_LIMIT priors remain — the same number the tab lists.
  const historyPromise = patientUuid
    ? loadPatientHistoryFromMedplum(patientUuid, { limit: PRIOR_VISIT_LIMIT + 1 }).catch(() => null)
    : Promise.resolve(null);

  // LONGITUDINAL ENRICHMENT OVERLAY (best-effort, fails soft to empty shapes).
  // buildEncounterView emits markers/vitalSeries/insightsBar/insightsRanked/
  // episode as tolerated empty stubs because its graph is THIS encounter only.
  // enrichConsultView runs the online-primary rebuild of the deleted disk-first
  // engines — RLS-scoped patient-wide Observation/CarePlan queries as this doctor
  // — computing the SAME DTO the consult panels (Markers/Vitals/Changes/Insights/
  // Plan tabs) already read. It never throws; each panel that has no data stays
  // its empty stub, so the consult can never regress on this path.
  // Its OWN reads need only patientId, so we hand it the history as a PROMISE:
  // its queries fire alongside the history read and it awaits the visits only
  // where the "what changed" diff actually needs them (one round trip, not two).
  const enrichmentPromise = patientUuid
    ? enrichConsultView({
      patientId: patientUuid,
      encounterId: encId,
      visits: historyPromise.then((h) => (h && h.visits) || []),
    })
    : Promise.resolve(null);

  // BUG-3 fix: ship the logged-in doctor's SAVED favourite meds/tests on the
  // consult bootstrap so the prescription chips render THEIR favourites, not the
  // screen's built-in DEFAULT_FAVS. ConsultCompact reads P('settings.favouriteMeds')
  // / P('settings.favouriteTests') off this payload; before this, the encounter
  // bootstrap carried no `settings` block, so that pick always returned null and
  // the doctor's saved favourites (e.g. a starred med) never reached the consult.
  // loadFavouritesForConsult returns the STORED lists only (nulls when the doctor
  // has saved none), so the screen's DEFAULT_FAVS fallback still fires for a
  // brand-new doctor. Read run-as-you (RLS: Basic?author=%profile) and fail-soft.
  // (Both it and deriveCurrentUser were STARTED at the top of this function.)
  const [qrs, media, history, enrichment, currentUser, favourites] = await Promise.all([
    sideFetch('QuestionnaireResponse'),
    sideFetch('Media'),
    historyPromise,
    enrichmentPromise,
    currentUserPromise,
    favouritesPromise,
  ]);
  graph.questionnaireResponses = qrs;
  graph.media = media;

  const encounter = buildEncounterView(graph);

  // priorContext = the PRIOR visits (current encounter filtered out here), same
  // shape the header strip + Prior·N badge read. Null keeps the empty stub.
  if (history) {
    const priorVisits = ((history.visits) || []).filter((v) => v && v.encounterId !== encId);
    const priorCtx = buildPriorContext(priorVisits);
    if (priorCtx) encounter.priorContext = priorCtx;
  }

  if (enrichment) {
    encounter.markers = enrichment.markers;
    encounter.vitalSeries = enrichment.vitalSeries;
    encounter.insights = enrichment.insights;
    encounter.insightsRanked = enrichment.insightsRanked;
    encounter.insightsBar = enrichment.insightsBar;
    encounter.episode = enrichment.episode;
  }

  // Same shape installBridge()/OPDBridge.pick('encounter.…') expects — the
  // consult screens are unchanged. currentUser drives OPDBridge.currentUser
  // (user.name in sign()/corrections) and is DERIVED from the live session
  // (deriveCurrentUser, cached); hospital drives OPDBridge.hospital
  // (ReferModalCompact's department list, consult-kit module reads).
  return {
    source: 'medplum-client-shaping',
    encounter,
    currentUser,
    hospital,
    settings: { favouriteMeds: favourites.favouriteMeds, favouriteTests: favourites.favouriteTests },
    // #13b: ConsultCompact renders a warning banner (with a retry) when true —
    // the chart loaded under RLS but the access service could not stamp the
    // compartment grant, so cross-compartment panels/sign may be incomplete.
    accessServiceDown,
  };
}
